import {
  MAINTENANCE_KINDS,
  MAINTENANCE_LIMITS,
  MAINTENANCE_ROLES,
  fr11Derived,
  validMaintenancePlan,
  validMaintenanceRecord,
  type KysFr11Row,
  type MaintenanceKind,
  type MaintenanceRecord,
  type MaintenanceRole,
  type ReportResult,
} from "@fts/core";
import { useEffect, useState, type FormEvent } from "react";
import { DateField } from "../../components/DateField";
import { Dialog, FormError, FormField } from "../../components/Dialog";
import { Icon } from "../../components/Icon";
import { useFeedback } from "../../components/Toast";
import { EmptyState, Note, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { formatDay } from "../../lib/export/sheet";
import { fr11FormExport, fr11Line, fr11Sheet } from "../../lib/kys-forms";
import { canManageFleet } from "../../lib/permissions";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import "../../styles/kys.css";
import { FormFooterButton, useFormFooter } from "./KysParts";
import { PrintBrand, ReportContext } from "./ReportParts";
import { ExportBar, FilterBar, Loading, parseFilters, siteLabel, todayLocal, useLoad, useRoleLabel, type ExportSpec } from "./shared";

const STATUS_TONE = { inactive: "neutral", maintenance_required: "crit", overdue: "crit", no_inspection: "warn", due_soon: "warn", ok: "ok", unknown: "neutral" } as const;

/**
 * KYS FR-11 "Envanter Bakım Takip" (ADR 0033): the vehicles of the scope with their maintenance plan
 * (interval, responsible ROLE), last/next periodic maintenance and yearly periodic inspection, and the status
 * (an open critical checklist fault shows "Bakım gerekli" — information only, never a lock). Fleet managers+
 * record maintenance and edit the plan. Exports: the paper form as PDF, and XLSX.
 */
export function KysFr11Report({ data, route }: { data: TenantData; route: Route }) {
  const { t, lang } = useI18n();
  const { provider, tenant, role } = useSession();
  const roleLabel = useRoleLabel();
  const { footer, reload: reloadFooter } = useFormFooter();
  const f = parseFilters(route, data);
  const tenantId = tenant?.id;
  const [tick, setTick] = useState(0);
  const [plan, setPlan] = useState<KysFr11Row | null>(null);
  const [records, setRecords] = useState<KysFr11Row | null>(null);
  const state = useLoad<ReportResult<KysFr11Row>>(tenantId ? () => provider.reportKysFr11({ tenantId, siteId: f.siteId }) : null, [provider, tenantId, f.siteId, tick]);
  const rows = state.data?.rows ?? [];
  const today = todayLocal();
  const scope = siteLabel(t, data, f.siteId);
  const manage = canManageFleet(role);
  const spec: ExportSpec | null = state.status === "ready" && tenantId
    ? {
        report: "kys_fr11",
        reportName: t("kys.fr11.title"),
        slug: "kys-fr11",
        part: today,
        tz: state.data!.tz,
        filters: [[t("reports.filter.site"), scope]],
        rows: rows.length,
        load: async (format) => {
          const r = await provider.exportKysFr11({ tenantId, siteId: f.siteId }, format);
          return {
            sheets: [fr11Sheet(t, lang, r.rows, today)],
            tz: r.tz,
            form: fr11FormExport(t, lang, r.rows, { today, tz: r.tz, footer, scope, generatedAt: new Date().toISOString(), role: roleLabel }),
          };
        },
      }
    : null;

  return (
    <>
      <PrintBrand title={t("kys.fr11.title")} tz={state.data?.tz ?? null} />
      <PageHeader title={t("kys.fr11.title")} subtitle={t("kys.fr11.subtitle")} eyebrow={t("nav.group.kys")} />
      <FilterBar data={data} path="/reports/kys-fr11" filters={f} operator={false} vehicles={false} dates={false} />
      <ReportContext chips={[[t("reports.filter.site"), scope]]} tz={state.data?.tz ?? null} sample={provider.mode === "demo"} period={formatDay(today, lang)} />
      <div className="kys-actions">
        <ExportBar spec={spec} disabled={rows.length === 0} buttons={["xlsx", "pdf"]} pdfLabel={t("kys.export.pdfForm")} />
        <FormFooterButton footer={footer} onSaved={reloadFooter} />
      </div>
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      <Panel flush meta={state.status === "ready" ? <span>{t("kys.fr11.count", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          <Loading />
        ) : rows.length === 0 ? (
          state.status === "error" ? null : <EmptyState compact icon="truck" title={t("kys.fr11.empty")} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table table-cards">
              <thead>
                <tr>
                  <th scope="col">{t("kys.fr11.col.no")}</th>
                  <th scope="col">{t("kys.fr11.col.name")}</th>
                  <th scope="col" className="col-hide-md">{t("kys.fr11.col.model")}</th>
                  <th scope="col" className="col-hide-md">{t("kys.fr11.col.location")}</th>
                  <th scope="col" className="col-hide-sm">{t("kys.fr11.col.responsible")}</th>
                  <th scope="col">{t("kys.fr11.col.nextPeriodic")}</th>
                  <th scope="col">{t("kys.fr11.col.nextInspection")}</th>
                  <th scope="col">{t("kys.fr11.col.status")}</th>
                  {manage && (
                    <th scope="col">
                      <span className="sr-only">{t("kys.fr11.actions")}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const l = fr11Line(t, lang, r, today);
                  const d = fr11Derived(r, today);
                  return (
                    <tr key={r.vehicleId}>
                      <td className="mono cell-title" data-label={t("kys.fr11.col.no")}>{l.equipmentNo}</td>
                      <td data-label={t("kys.fr11.col.name")}>{l.name}</td>
                      <td className="col-hide-md" data-label={t("kys.fr11.col.model")}>{l.model}</td>
                      <td className="col-hide-md" data-label={t("kys.fr11.col.location")}>{l.location}</td>
                      <td className="col-hide-sm" data-label={t("kys.fr11.col.responsible")}>{l.responsible}</td>
                      <td className="nowrap" data-label={t("kys.fr11.col.nextPeriodic")}>
                        {l.nextPeriodic} <span className="muted small">({t("kys.fr11.last")}: {l.lastPeriodic})</span>
                      </td>
                      <td className="nowrap" data-label={t("kys.fr11.col.nextInspection")}>
                        {l.nextInspection} <span className="muted small">({t("kys.fr11.last")}: {l.lastInspection})</span>
                      </td>
                      <td data-label={t("kys.fr11.col.status")}>
                        <span className={`badge tone-${STATUS_TONE[d.status]}`}>{l.status}</span>
                      </td>
                      {manage && (
                        <td className="col-actions">
                          <button type="button" className="link-button" onClick={() => setRecords(r)}>
                            {t("kys.fr11.records")}
                          </button>{" "}
                          <button type="button" className="link-button" onClick={() => setPlan(r)}>
                            {t("kys.fr11.plan")}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("kys.fr11.footnote")}</p>
      {plan && <PlanDialog row={plan} onClose={() => setPlan(null)} onSaved={() => (setPlan(null), setTick((x) => x + 1))} />}
      {records && <RecordsDialog row={records} onClose={() => setRecords(null)} onChanged={() => setTick((x) => x + 1)} />}
    </>
  );
}

function PlanDialog({ row, onClose, onSaved }: { row: KysFr11Row; onClose: () => void; onSaved: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  const feedback = useFeedback();
  const [interval, setInterval] = useState(row.intervalMonths ? String(row.intervalMonths) : "");
  const [roleValue, setRole] = useState<MaintenanceRole | "">(row.responsibleRole ?? "");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const months = interval.trim() ? Number(interval) : null;
  const input = { vehicleId: row.vehicleId, intervalMonths: months, responsibleRole: roleValue || null };
  const valid = validMaintenancePlan(input);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveMaintenancePlan(input);
      feedback.ok(t("kys.fr11.planSaved"));
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t("kys.fr11.planTitle", { vehicle: row.fleetNo })}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || !valid}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="kys-interval" label={t("kys.fr11.col.intervalMonths")} hint={t("kys.fr11.intervalHint")} error={interval && !valid ? t("kys.fr11.intervalInvalid") : null} optional>
        <input id="kys-interval" type="number" inputMode="numeric" min={MAINTENANCE_LIMITS.intervalMinMonths} max={MAINTENANCE_LIMITS.intervalMaxMonths} value={interval} onChange={(e) => setInterval(e.target.value)} data-autofocus />
      </FormField>
      <FormField id="kys-role" label={t("kys.fr11.col.responsible")} hint={t("kys.fr11.roleHint")} optional>
        <select id="kys-role" value={roleValue} onChange={(e) => setRole(e.target.value as MaintenanceRole | "")}>
          <option value="">{t("common.empty")}</option>
          {MAINTENANCE_ROLES.map((r) => (
            <option key={r} value={r}>
              {t(`kys.mrole.${r}`)}
            </option>
          ))}
        </select>
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function RecordsDialog({ row, onClose, onChanged }: { row: KysFr11Row; onClose: () => void; onChanged: () => void }) {
  const { t, lang } = useI18n();
  const { provider } = useSession();
  const feedback = useFeedback();
  const today = todayLocal();
  const [list, setList] = useState<MaintenanceRecord[] | null>(null);
  const [kind, setKind] = useState<MaintenanceKind>("periodic");
  const [doneOn, setDoneOn] = useState(today);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  // Correction (records are never deleted): the new record voids this one, with a reason.
  const [correcting, setCorrecting] = useState<MaintenanceRecord | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const load = () => provider.listMaintenanceRecords(row.vehicleId).then(setList, () => setList([]));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.vehicleId]);
  const voids = correcting ? { voidsRecordId: correcting.id, voidReason: reason.trim() || null } : {};
  const valid = validMaintenanceRecord({ kind, doneOn, note: note.trim() || null, ...voids }, today);
  const startCorrection = (r: MaintenanceRecord) => {
    setCorrecting(r);
    setKind(r.kind);
    setDoneOn(r.doneOn);
    setNote(r.note ?? "");
    setReason("");
  };
  const stopCorrection = () => (setCorrecting(null), setReason(""), setNote(""), setDoneOn(today));
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await provider.addMaintenanceRecord({ vehicleId: row.vehicleId, kind, doneOn, note: note.trim() || null, ...voids });
      feedback.ok(t("kys.fr11.recordSaved"));
      stopCorrection();
      await load();
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      wide
      title={t("kys.fr11.recordsTitle", { vehicle: row.fleetNo })}
      onClose={() => !busy && onClose()}
      onSubmit={add}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.close")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || !valid}>
            {busy ? t("common.working") : correcting ? t("kys.fr11.saveCorrection") : t("kys.fr11.addRecord")}
          </button>
        </>
      }
    >
      {correcting && (
        <p className="kys-correcting" role="status">
          {t("kys.fr11.correcting", { date: formatDay(correcting.doneOn, lang), kind: t(`kys.kind.${correcting.kind}`) })}{" "}
          <button type="button" className="link-button" onClick={stopCorrection}>
            {t("kys.fr11.cancelCorrection")}
          </button>
        </p>
      )}
      <div className="kys-record-form">
        <FormField id="kys-kind" label={t("kys.fr11.kind")}>
          <select id="kys-kind" value={kind} onChange={(e) => setKind(e.target.value as MaintenanceKind)}>
            {MAINTENANCE_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`kys.kind.${k}`)}
              </option>
            ))}
          </select>
        </FormField>
        <FormField id="kys-date" label={t("kys.fr11.doneOn")}>
          <DateField id="kys-date" value={doneOn} max={today} required onChange={setDoneOn} />
        </FormField>
        <FormField id="kys-note" label={t("kys.fr11.note")} hint={t("kys.fr11.noteHint")} optional>
          <input id="kys-note" value={note} maxLength={MAINTENANCE_LIMITS.noteMax} onChange={(e) => setNote(e.target.value)} />
        </FormField>
        {correcting && (
          <FormField id="kys-reason" label={t("kys.fr11.voidReason")} hint={t("kys.fr11.voidReasonHint")}>
            <input id="kys-reason" value={reason} maxLength={MAINTENANCE_LIMITS.voidReasonMax} required onChange={(e) => setReason(e.target.value)} />
          </FormField>
        )}
      </div>
      {error !== null && <FormError error={error} />}
      <h3 className="kys-subhead">{t("kys.fr11.history")}</h3>
      {list === null ? (
        <Loading />
      ) : list.length === 0 ? (
        <Note>{t("kys.fr11.noRecords")}</Note>
      ) : (
        <ul className="kys-records">
          {list.map((r) => (
            <li key={r.id} className={r.voided ? "is-voided" : undefined}>
              <span className="nowrap kys-record-date">{formatDay(r.doneOn, lang)}</span>
              <span className="badge tone-neutral">{t(`kys.kind.${r.kind}`)}</span>
              {r.voided && <span className="badge tone-warn">{t("kys.fr11.voided")}</span>}
              <span className="kys-record-note">
                {r.note ?? ""}
                {r.voidReason && <span className="small"> · {t("kys.fr11.correctionOf", { reason: r.voidReason })}</span>}
              </span>
              {!r.voided && (
                <button type="button" className="link-button" onClick={() => startCorrection(r)} aria-label={t("kys.fr11.correctTitle", { date: formatDay(r.doneOn, lang) })}>
                  <Icon name="edit" size={15} /> {t("kys.fr11.correct")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="field-hint">{t("kys.fr11.noDelete")}</p>
    </Dialog>
  );
}
